import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { Box } from "@mui/material";
import { GoMakeDialog, GomakeTextInput } from "@/components";
import { ReOrderIcon } from "@/icons";
import { useSnackBar } from "@/hooks";
import { userProfileState } from "@/store/user-profile";
import {
  getRollDirectionImageUrl,
  mapRollDirectionValues,
  normalizeLang,
  upsertRollDirectionTranslation,
} from "./roll-direction-utils";

const RollDirectionOptionsModal = ({
  openModal,
  onClose,
  modalTitle,
  selectedParameter,
  updatedValuesConfigsForParameters,
  selectedSectonId,
  selectedSubSection,
}: any) => {
  const { t, i18n } = useTranslation();
  const userProfile = useRecoilValue(userProfileState);
  const systemLang = normalizeLang(
    userProfile?.systemLang || i18n.language || "en"
  );
  const { setSnackbarStateValue } = useSnackBar();
  const [valuesConfigs, setValuesConfigs] = useState<any[]>([]);

  useEffect(() => {
    if (!openModal) {
      return;
    }
    const mapped = mapRollDirectionValues(
      selectedParameter?.valuesConfigs,
      systemLang
    );
    setValuesConfigs(mapped);
  }, [openModal, selectedParameter, systemLang]);

  const changeName = (index: number, value: string) => {
    setValuesConfigs((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        updateName: value,
        translations: upsertRollDirectionTranslation(
          next[index].translations,
          systemLang,
          value
        ),
      };
      return next;
    });
  };

  const reorder = (list: any[], startIndex: number, endIndex: number) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const onDragEnd = (result: any) => {
    if (!result.destination) {
      return;
    }
    setValuesConfigs(
      reorder(valuesConfigs, result.source.index, result.destination.index)
    );
  };

  const getItemStyle = (isDragging: boolean, draggableStyle: any) => {
    const { width: _ignoredWidth, left: _ignoredLeft, ...restDragStyle } =
      draggableStyle ?? {};
    return {
      boxSizing: "border-box" as const,
      padding: 8,
      marginBlockEnd: 12,
      userSelect: "none" as const,
      display: "flex",
      flexDirection: "row" as const,
      alignItems: "center",
      gap: 12,
      borderRadius: 8,
      backgroundColor: isDragging ? "#FAFAFA" : "",
      ...restDragStyle,
      width: "100%",
      maxWidth: "100%",
      top: 0,
      left: "auto",
      right: "auto",
      insetInline: 0,
      position: "relative" as const,
    };
  };

  const isUpdateNameRepeated = (updateName: string, currentIndex: number) => {
    const formatted = (updateName ?? "").replace(/\s/g, "").toLowerCase();
    if (!formatted) {
      return false;
    }
    return valuesConfigs.some(
      (item, index) =>
        index !== currentIndex &&
        (item.updateName ?? "").replace(/\s/g, "").toLowerCase() === formatted
    );
  };

  const handleSave = () => {
    const hasEmptyName = valuesConfigs.some(
      (item) => !(item?.updateName ?? "").trim()
    );
    if (hasEmptyName) {
      setSnackbarStateValue({
        state: true,
        message: t("products.offsetPrice.admin.fillValue", {
          defaultValue: "Please fill all values",
        }),
        type: "error",
      });
      return;
    }

    const hasRepeated = valuesConfigs.some((item, index) =>
      isUpdateNameRepeated(item?.updateName, index)
    );
    if (hasRepeated) {
      setSnackbarStateValue({
        state: true,
        message: t("products.offsetPrice.admin.valuesErrorMsg", {
          defaultValue: "Values must be unique",
        }),
        type: "error",
      });
      return;
    }

    const payloadConfigs = valuesConfigs.map((item) => ({
      ...item,
      updateName: (item.updateName ?? "").trim(),
      translations: upsertRollDirectionTranslation(
        item.translations,
        systemLang,
        (item.updateName ?? "").trim()
      ),
    }));
    updatedValuesConfigsForParameters(selectedSectonId, selectedSubSection, {
      ...selectedParameter,
      valuesConfigs: payloadConfigs,
    });
    onClose();
  };

  return (
    <GoMakeDialog
      open={openModal}
      onClose={onClose}
      title={modalTitle}
      onPrimaryAction={handleSave}
      primaryButtonLabel={t("common.save")}
      cancelButtonLabel={t("modal.cancel")}
      maxWidth="sm"
      fullWidth
      customStyles={{
        paper: {
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          maxHeight: "80vh",
        },
        content: {
          overflow: "hidden",
          minHeight: 0,
          flex: 1,
          width: "100%",
        },
      }}
    >
      <Box
        className="custom-scrollbar"
        sx={{
          width: "100%",
          maxHeight: "min(520px, calc(80vh - 160px))",
          overflowY: "auto",
          overflowX: "hidden",
          pt: 1,
          paddingInlineEnd: "8px",
          scrollbarGutter: "stable",
        }}
      >
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="roll-direction-values">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                style={{ width: "100%" }}
              >
                {valuesConfigs.map((item, index) => {
                  const imageUrl = getRollDirectionImageUrl(item);

                  return (
                    <Draggable
                      key={item.id}
                      draggableId={String(item.id)}
                      index={index}
                    >
                      {(dragProvided, snapshot) => (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          style={getItemStyle(
                            snapshot.isDragging,
                            dragProvided.draggableProps.style
                          )}
                        >
                          <Box
                            {...dragProvided.dragHandleProps}
                            sx={{
                              cursor: "grab",
                              display: "flex",
                              alignItems: "center",
                              flexShrink: 0,
                            }}
                            aria-label={t(
                              "products.offsetPrice.admin.reorder",
                              { defaultValue: "Reorder" }
                            )}
                          >
                            <ReOrderIcon />
                          </Box>
                          {imageUrl ? (
                            <Box
                              sx={{
                                width: 56,
                                height: 56,
                                flexShrink: 0,
                                borderRadius: "8px",
                                overflow: "hidden",
                                border: "1px solid #E6E6F0",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "#FFF",
                              }}
                            >
                              <Box
                                component="img"
                                src={imageUrl}
                                alt={item?.updateName ?? ""}
                                sx={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "contain",
                                }}
                              />
                            </Box>
                          ) : null}
                          <Box sx={{ flex: "1 1 auto", minWidth: 0, width: "100%" }}>
                            <GomakeTextInput
                              direction={t("direction")}
                              style={{
                                width: "100%",
                                borderRadius: 4,
                                height: 40,
                              }}
                              sx={{ width: "100%" }}
                              placeholder={t(
                                "products.profits.exceptions.enterValue"
                              )}
                              value={item?.updateName ?? ""}
                              onChange={(e: any) =>
                                changeName(index, e.target.value)
                              }
                            />
                          </Box>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </Box>
    </GoMakeDialog>
  );
};

export { RollDirectionOptionsModal };
